import React, { useEffect, useLayoutEffect, useRef, useMemo, useState } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import {
  buildVexBeamsFromCandidates,
  beatIdxFromDivisions,
  drawVexBeams,
  type StaffBeamCandidate,
} from "@/lib/staffBeams";
import {
  buildHighwayTimeLayout,
  getMeasureLengthDivisions,
  staffVexKeyFromNote,
  type MeasureInfo,
  type TimedMusicXmlNote,
} from "../lib/songEngine";
import { staffPixelXAtTime } from "../lib/songPlayback";
import { staffGeometrySignature } from "../lib/staffGeometrySignature";
import { SONG_STAFF_HEIGHT_PX, SONG_STAFF_STAVE_SCALE } from "../lib/songViewDefaults";
import {
  SONG_STAFF_BASS_BLOCK_PX,
  SONG_STAFF_TREBLE_HEIGHT_PX,
} from "../lib/songStaffLayout";
import { pitchClassColor } from "../lib/pitchClassColors";

type VexNoteMapEntry = {
  sn: InstanceType<typeof StaveNote>;
  flatIndices: number[];
  /** Silencio generado antes de una nota real (mismo instante que `measureStartGlobal + hueco`). */
  gapStartTime?: number;
};

interface SongStaffRendererProps {
  notes: TimedMusicXmlNote[];
  divisions: number;
  timeSignature?: { beats: number; beatType: number };
  currentNoteIndex?: number;
  /** Resalta todas las notas de este instante (acordes / voces). */
  activeStartTime?: number;
  width?: number;
  /** Playhead fijo en el contenedor padre; no dibujar cursor en SVG. */
  hidePlayhead?: boolean;
  darkMode?: boolean;
  clefOctaveChange?: number;
  /** Clave declarada en el XML (0 = sol normal, -1 = 8vb). */
  xmlClefOctaveChange?: number;
  /** Metadatos de compases (anacrusa, longitud real, etc.). */
  measureInfos?: MeasureInfo[];
  /** Mismo px/beat que el mástil para alinear compás a compás. */
  pixelsPerBeat?: number;
  /**
   * Ataque → X en píxeles (origen = borde izquierdo del SVG, mismo espacio que scroll del pentagrama).
   * Incluye min entre voces cuando comparten startTime.
   */
  onStaffTimeGeometry?: (geometry: Array<{ startTime: number; x: number }>) => void;
  /**
   * Borde izquierdo del contenido rítmico del compás 1 (tras clave y cifra), en px del mismo espacio
   * que la geometría — alinea pausa visual del mástil con la del pentagrama.
   */
  onStaffLayoutMeta?: (meta: { firstMeasureContentLeftPx: number; totalScrollWidthPx: number }) => void;
  /** Color de acento para el resplandor de nota activa (solo CSS, sin redibujar Vex). */
  activeGlowAccent?: string;
  noteFeedback?: Record<number, "correct" | "wrong">;
  /** Reserva pentagrama en clave de fa (piano / registro grave). */
  showBassStaff?: boolean;
}

const DURATION_MAP: Record<string, string> = {
  whole: "w", half: "h", quarter: "q", eighth: "8",
  "16th": "16", "32nd": "32", "64th": "64",
};

/** Silencio VexFlow para una duración dada */
function restNote(duration: string): InstanceType<typeof StaveNote> {
  return new StaveNote({ keys: ["b/4"], duration: duration + "r" });
}

/** Valor rítmico en beats (negra = 1) */
function beatValue(type: string): number {
  switch (type) {
    case "whole": return 4;
    case "half": return 2;
    case "quarter": return 1;
    case "eighth": return 0.5;
    case "16th": return 0.25;
    case "32nd": return 0.125;
    default: return 1;
  }
}

const STAVE_SCALE = SONG_STAFF_STAVE_SCALE;
const PADDING_X = 24;
const BEAT_WIDTH = 80;
const STAVE_HEIGHT = SONG_STAFF_HEIGHT_PX;
/** Tras formatear, `getX()` cae ~en el borde izquierdo del grupo; desplazamos hacia el centro de la cabeza (coords pre-escala). */
const STAFF_NOTEHEAD_CENTER_BIAS_PRESCALE = 8.25;
/** Fallback vertical si aún no hay bbox de Vex (mismo lienzo que STAVE_HEIGHT). */
const STAFF_NOTEHEAD_HALO_CENTER_Y_PX = Math.round(STAVE_HEIGHT * 0.385);

/** Extrae centro (px del SVG scroll) de cada cabeza tras el layout de VexFlow. */
function noteHeadCentersFromStaveNote(sn: InstanceType<typeof StaveNote>): { x: number; y: number }[] {
  const heads = sn.noteHeads;
  if (!heads?.length) return [];
  const out: { x: number; y: number }[] = [];
  for (const nh of heads) {
    const bb = nh.getBoundingBox();
    const w = bb.getW();
    const h = bb.getH();
    if (w <= 0 && h <= 0) continue;
    const cx = (bb.getX() + w / 2) * STAVE_SCALE;
    const cy = (bb.getY() + h / 2) * STAVE_SCALE;
    out.push({ x: cx, y: cy });
  }
  return out;
}

function pushHalosDistinct(
  bucket: Record<number, { x: number; y: number }[]>,
  startTime: number,
  centers: { x: number; y: number }[]
) {
  if (centers.length === 0) return;
  const list = bucket[startTime] ?? (bucket[startTime] = []);
  for (const c of centers) {
    if (!list.some((p) => Math.hypot(p.x - c.x, p.y - c.y) < 3)) {
      list.push(c);
    }
  }
}

/** Halos multi-cabeza: una entrada real por figura (acordes / varias voces en el mismo instante). */
function StaffActiveGlowOverlay({
  centersByStartTime,
  geometry,
  activeStartTime,
  darkMode,
  accent = "#f4d4bc",
  softOnly = false,
}: {
  centersByStartTime: Record<number, { x: number; y: number }[]>;
  geometry: Array<{ startTime: number; x: number }>;
  activeStartTime: number;
  darkMode: boolean;
  accent?: string;
  softOnly?: boolean;
}) {
  const tHighlight = useMemo(() => {
    if (activeStartTime >= 0) return activeStartTime;
    return -1;
  }, [activeStartTime]);

  const centers = useMemo(() => {
    if (tHighlight < 0) return [];
    const fromVex = centersByStartTime[tHighlight];
    if (fromVex && fromVex.length > 0) return fromVex;
    if (geometry.length === 0) return [];
    const x = staffPixelXAtTime(geometry, tHighlight);
    return [{ x, y: STAFF_NOTEHEAD_HALO_CENTER_Y_PX }];
  }, [tHighlight, centersByStartTime, geometry]);

  if (tHighlight < 0 || centers.length === 0) return null;

  const outer = softOnly ? 58 : 48;
  const inner = softOnly ? 38 : 30;

  return (
    <>
      {centers.map((pos, idx) => (
        <div
          key={`${pos.x.toFixed(1)}-${pos.y.toFixed(1)}-${idx}`}
          className="pointer-events-none absolute z-0"
          style={{
            left: pos.x,
            top: pos.y,
            transform: "translate(-50%, -50%)",
            width: outer,
            height: outer,
            ["--staff-halo-accent" as string]: accent,
          }}
          aria-hidden
        >
          <div
            className={`staff-notehead-halo-outer absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
              darkMode ? "mix-blend-screen" : "mix-blend-multiply"
            }`}
            style={{ width: outer, height: outer }}
          />
          <div
            className={`staff-notehead-halo-inner absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full ${
              darkMode ? "mix-blend-screen" : "mix-blend-multiply"
            }`}
            style={{ width: inner, height: inner }}
          />
        </div>
      ))}
    </>
  );
}

function StaffFeedbackOverlay({
  centersByNoteIndex,
  feedback,
}: {
  centersByNoteIndex: Record<number, { x: number; y: number }>;
  feedback: Record<number, "correct" | "wrong">;
}) {
  return (
    <>
      {Object.entries(feedback).map(([idx, status]) => {
        const pos = centersByNoteIndex[Number(idx)];
        if (!pos) return null;
        const correct = status === "correct";
        return (
          <div
            key={`${idx}-${status}`}
            className={`pointer-events-none absolute z-[2] rounded-full staff-feedback-note ${
              correct ? "staff-feedback-note-correct" : "staff-feedback-note-wrong"
            }`}
            style={{
              left: pos.x,
              top: pos.y,
              transform: "translate(-50%, -50%)",
              width: 24,
              height: 18,
            }}
          />
        );
      })}
    </>
  );
}

const SongStaffRenderer = (props: SongStaffRendererProps & { _ref?: React.RefObject<HTMLDivElement | null> }) => {
    const {
      notes,
      divisions,
      timeSignature = { beats: 4, beatType: 4 },
      currentNoteIndex = -1,
      activeStartTime = -1,
      width = 800,
      _ref,
      hidePlayhead = false,
      darkMode = false,
      measureInfos = [],
      pixelsPerBeat = BEAT_WIDTH,
      onStaffTimeGeometry,
      onStaffLayoutMeta,
      activeGlowAccent = "#f4d4bc",
      noteFeedback = {},
      showBassStaff = false,
    } = props;
    const canvasHeight = showBassStaff
      ? SONG_STAFF_TREBLE_HEIGHT_PX + SONG_STAFF_BASS_BLOCK_PX
      : SONG_STAFF_HEIGHT_PX;
    const trebleStaveY = 12;
    const bassStaveY =
      12 + Math.round((SONG_STAFF_TREBLE_HEIGHT_PX - 48) / SONG_STAFF_STAVE_SCALE);
    /** Guitarra: escritura 8vb desde MIDI de sonido; piano: octava científica en ambas claves. */
    const guitar8vbWritten = !showBassStaff;
    /** Contenedor con `scrollLeft` (expuesto al padre vía `_ref`). */
    const scrollWrapRef = useRef<HTMLDivElement>(null);
    const vexMountRef = useRef<HTMLDivElement>(null);
    const onStaffTimeGeometryRef = useRef(onStaffTimeGeometry);
    onStaffTimeGeometryRef.current = onStaffTimeGeometry;
    const onStaffLayoutMetaRef = useRef(onStaffLayoutMeta);
    onStaffLayoutMetaRef.current = onStaffLayoutMeta;
    const lastPublishedGeomSigRef = useRef("");

    const [staffGeometry, setStaffGeometry] = useState<Array<{ startTime: number; x: number }>>([]);
    const [headGlowCentersByTime, setHeadGlowCentersByTime] = useState<
      Record<number, { x: number; y: number }[]>
    >({});
    const [headCentersByNoteIndex, setHeadCentersByNoteIndex] = useState<
      Record<number, { x: number; y: number }>
    >({});

    useLayoutEffect(() => {
      if (_ref) (_ref as React.MutableRefObject<HTMLDivElement | null>).current = scrollWrapRef.current;
    });

  const beatsPerMeasure = timeSignature.beats;
  const beatType = timeSignature.beatType;

  // ─── 1. Agrupar notas por compás usando startTime ───
  type MeasureRender = {
    notes: TimedMusicXmlNote[];
    info: MeasureInfo;
    /** ¿Compás incompleto (anacrusa / final cortado)? */
    isPickup: boolean;
    /** Divisiones útiles que ocupará el contenido al renderizar. */
    contentLength: number;
  };

  const measures = useMemo<MeasureRender[]>(() => {
    if (notes.length === 0) return [];
    const fallbackLen = getMeasureLengthDivisions(beatsPerMeasure, beatType, divisions);

    const infos: MeasureInfo[] = measureInfos.length
      ? measureInfos
      : (() => {
          const last = notes[notes.length - 1].startTime;
          const count = Math.floor(last / fallbackLen) + 1;
          return Array.from({ length: count }, (_, m) => ({
            startTime: m * fallbackLen,
            length: fallbackLen,
            contentLength: fallbackLen,
            isPickup: false,
            timeSignature: { beats: beatsPerMeasure, beatType },
            divisions,
          }));
        })();

    const buckets: TimedMusicXmlNote[][] = infos.map(() => []);
    for (const n of notes) {
      let lo = 0;
      let hi = infos.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const info = infos[mid];
        if (n.startTime < info.startTime - 0.001) hi = mid - 1;
        else if (n.startTime >= info.startTime + info.length - 0.001) lo = mid + 1;
        else {
          buckets[mid].push(n);
          break;
        }
      }
    }

    return infos.map((info, idx) => ({
      notes: buckets[idx],
      info,
      isPickup: info.isPickup,
      contentLength: info.isPickup ? info.contentLength : info.length,
    }));
  }, [notes, divisions, beatsPerMeasure, beatType, measureInfos]);

  const highwayLayout = useMemo(() => {
    if (measureInfos.length === 0) return null;
    return buildHighwayTimeLayout(measureInfos, divisions, pixelsPerBeat);
  }, [measureInfos, divisions, pixelsPerBeat]);

  // ─── 2. Calcular anchos por compás ───
  const { measureWidths, totalWidth } = useMemo(() => {
    const widths = measures.map((m, idx) => {
      const baseFromHighway =
        highwayLayout && highwayLayout.measureVisualWidths[idx] !== undefined
          ? highwayLayout.measureVisualWidths[idx]
          : (m.contentLength / divisions) * BEAT_WIDTH;
      const realNotes = m.notes.filter((n) => !n.isRest);
      const accidentalBonus = realNotes.filter((n) => n.alter !== undefined).length * 14;
      const clefBonus = idx === 0 ? 90 : 0;
      const pickupPadding = m.isPickup ? 26 : 0;
      const w = baseFromHighway + accidentalBonus + clefBonus + pickupPadding + 56;
      return Math.max(m.isPickup ? 110 : 200, w);
    });
    const tw = PADDING_X * 2 + widths.reduce((a, b) => a + b, 0);
    return { measureWidths: widths, totalWidth: tw };
  }, [measures, divisions, highwayLayout]);

  // ─── 3. Renderizar Vex (solo dependencias estructurales; por lotes para no bloquear UI) ───
  useEffect(() => {
    if (!vexMountRef.current || notes.length === 0) return;

    let cancelled = false;
    let idleHandle: number | ReturnType<typeof setTimeout> | undefined;

    const MEASURES_PER_YIELD = 2;

    const yieldToMain = () =>
      new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

    const renderVex = async () => {
      if (cancelled || !vexMountRef.current) return;
    vexMountRef.current.innerHTML = "";

    const renderer = new Renderer(vexMountRef.current, Renderer.Backends.SVG);
    renderer.resize(totalWidth, canvasHeight);
    const context = renderer.getContext();
    context.scale(STAVE_SCALE, STAVE_SCALE);

    const staffLineColor = darkMode ? "rgba(240, 243, 245, 0.65)" : "#000000";
    const notationColor = darkMode ? "#F0F3F5" : "#000000";
    const restColor = darkMode ? "rgba(240, 243, 245, 0.38)" : "#000000";

    const staveY = trebleStaveY;
    let accumulatedX = 0;
    let globalNoteIndex = 0;
    /**
     * Mapas separados: los silencios "gap" no deben pisar la X de una nota real
     * en el mismo instante (anacrusa: el silencio inicial y la nota pueden compartir t=0).
     */
    const xGapByTime = new Map<number, number>();
    /** Solo notas con altura (no silencios XML): para playback usar la X más a la derecha por instante (evita que un silencio de otra voz robe la columna de la melodía). */
    const xPitchByTime = new Map<number, number>();
    /** Silencios con instante explícito en el XML (otra voz). */
    const xRestByTime = new Map<number, number>();
    /** Mínima X de cualquier glifo en el compás 0 (inicio del contenido rítmico tras clave/compás). */
    let firstMeasureMinContentPx: number | undefined;
    const headGlowByTime: Record<number, { x: number; y: number }[]> = {};
    const headCenterByIndex: Record<number, { x: number; y: number }> = {};

    for (let mIdx = 0; mIdx < measures.length; mIdx++) {
      if (cancelled) return;
      const measure = measures[mIdx];
      const mNotes = measure.notes;
      const isPickup = measure.isPickup;
      const measureLengthDiv = measure.info.length;
      const measureStartGlobal = measure.info.startTime;

      const mWidth = measureWidths[mIdx];
      const measureStartPx = PADDING_X + accumulatedX;
      const x = measureStartPx / STAVE_SCALE;
      const staveW = mWidth / STAVE_SCALE;
      accumulatedX += mWidth;

      const stave = new Stave(x, staveY, staveW);
      if (mIdx === 0) {
        stave.addClef("treble", "default", "8vb");
        stave.addTimeSignature(`${beatsPerMeasure}/${beatType}`);
      }
      context.setStrokeStyle(staffLineColor);
      context.setFillStyle(staffLineColor);
      stave.setContext(context).draw();

      if (showBassStaff) {
        const bassStave = new Stave(x, bassStaveY, staveW);
        if (mIdx === 0) {
          bassStave.addClef("bass");
        }
        context.setStrokeStyle(staffLineColor);
        context.setFillStyle(staffLineColor);
        bassStave.setContext(context).draw();
        if (mIdx === 0) {
          const braceX = x - 10;
          context.setStrokeStyle(staffLineColor);
          context.setLineWidth(1.25);
          context.beginPath();
          context.moveTo(braceX, trebleStaveY + 8);
          context.lineTo(braceX, bassStaveY + 72);
          context.stroke();
        }
      }

      const divPerMeasure = measureLengthDiv;
      /** Anacrusa: solo el contenido del XML, sin rellenar hasta completar el compás. */
      const renderEnd = isPickup ? measure.info.contentLength : measureLengthDiv;
      const pickupLeadDiv = 0;

      // ─── Multi-voice rendering: una voz VexFlow por cada voz del MusicXML ───
      const voiceNumbers = [...new Set(mNotes.map((n) => n.voice ?? 1))].sort((a, b) => a - b);

      const vexVoices: InstanceType<typeof Voice>[] = [];
      const allNoteMaps: VexNoteMapEntry[] = [];
      const voiceBeamCandidates: Map<number, { note: InstanceType<typeof StaveNote>; beatIdx: number }[]> = new Map();

      for (const vNum of voiceNumbers) {
        const vNotesWithIdx = mNotes
          .map((n, idx) => ({ note: n, flatIdx: globalNoteIndex + idx }))
          .filter((item) => (item.note.voice ?? 1) === vNum)
          .sort((a, b) => a.note.startTime - b.note.startTime);

        const tickables: InstanceType<typeof StaveNote>[] = [];
        const noteMap: VexNoteMapEntry[] = [];
        const beamCandidates: { note: InstanceType<typeof StaveNote>; beatIdx: number }[] = [];
        let currentDiv = 0;
        let vi = 0;

        while (vi < vNotesWithIdx.length) {
          const { note, flatIdx } = vNotesWithIdx[vi];
          const relStart = note.startTime - measureStartGlobal;
          const effRelStart = relStart + pickupLeadDiv;

          // Rellenar silencios si hay hueco antes de la siguiente figura en esta voz.
          if (effRelStart > currentDiv) {
            const gapBeats = (effRelStart - currentDiv) / divisions;
            let remaining = gapBeats;
            const gapStartDiv = currentDiv;
            let firstGapRest = true;
            while (remaining > 0.001) {
              let dur: "q" | "8" | "16";
              if (remaining >= 1) {
                dur = "q";
                remaining -= 1;
              } else if (remaining >= 0.5) {
                dur = "8";
                remaining -= 0.5;
              } else if (remaining >= 0.25) {
                dur = "16";
                remaining -= 0.25;
              } else break;
              const rn = restNote(dur);
              if (darkMode) rn.setStyle({ fillStyle: restColor, strokeStyle: restColor });
              tickables.push(rn);
              noteMap.push({
                sn: rn,
                flatIndices: [],
                ...(firstGapRest ? { gapStartTime: measureStartGlobal + gapStartDiv } : {}),
              });
              firstGapRest = false;
            }
            currentDiv = effRelStart;
          }

          // Agrupar acordes dentro de la misma voz (mismo startTime)
          const chordItems: typeof vNotesWithIdx = [vNotesWithIdx[vi]];
          let vj = vi + 1;
          while (
            vj < vNotesWithIdx.length &&
            !note.isRest &&
            !vNotesWithIdx[vj].note.isRest &&
            vNotesWithIdx[vj].note.startTime === note.startTime
          ) {
            chordItems.push(vNotesWithIdx[vj]);
            vj++;
          }

          const head = chordItems[0].note;
          const duration = (DURATION_MAP[head.type] || "q") + (head.hasDot ? "d" : "");
          let sn: InstanceType<typeof StaveNote>;

          if (head.isRest) {
            sn = new StaveNote({ keys: ["b/4"], duration: duration + "r" });
            if (darkMode) {
              sn.setStyle({ fillStyle: restColor, strokeStyle: restColor });
            }
          } else {
            const keys = chordItems.map((item) =>
              staffVexKeyFromNote(item.note, guitar8vbWritten)
            );
            const stemDir = vNum <= 2 ? 1 : -1; // voces 1-2 arriba, 3-4 abajo
            sn = new StaveNote({ keys, duration, autoStem: false, stemDirection: stemDir });
            chordItems.forEach((item, kIdx) => {
              if (item.note.alter === 1) sn.addModifier(new Accidental("#"), kIdx);
              else if (item.note.alter === -1) sn.addModifier(new Accidental("b"), kIdx);
            });

            // Candidato para beam (dentro de su voz)
            const couldBeam = head.type === "eighth" || head.type === "16th" || head.type === "32nd";
            if (couldBeam) {
              const beatIdx = beatIdxFromDivisions(effRelStart, divisions);
              beamCandidates.push({ note: sn, beatIdx });
            }
          }

          const flatIndices = chordItems.map((item) => item.flatIdx);
          if (!head.isRest) {
            const noteColor = pitchClassColor(head.midi > 0 ? head.midi : 60);
            sn.setStyle({ fillStyle: noteColor, strokeStyle: noteColor });
          }
          tickables.push(sn);
          noteMap.push({ sn, flatIndices });
          currentDiv = effRelStart + head.duration;
          vi = vj;
        }

        // Silencios finales solo en compases completos (no rellenar anacrusa).
        const missingDiv = isPickup ? 0 : renderEnd - currentDiv;
        if (missingDiv > 0) {
          let remaining = missingDiv / divisions;
          const trailStartDiv = currentDiv;
          let firstTrailRest = true;
          while (remaining > 0.001) {
            let dur: "q" | "8" | "16";
            if (remaining >= 1) {
              dur = "q";
              remaining -= 1;
            } else if (remaining >= 0.5) {
              dur = "8";
              remaining -= 0.5;
            } else if (remaining >= 0.25) {
              dur = "16";
              remaining -= 0.25;
            } else break;
            const rn = restNote(dur);
            if (darkMode) rn.setStyle({ fillStyle: restColor, strokeStyle: restColor });
            tickables.push(rn);
            noteMap.push({
              sn: rn,
              flatIndices: [],
              ...(firstTrailRest ? { gapStartTime: measureStartGlobal + trailStartDiv } : {}),
            });
            firstTrailRest = false;
          }
        }

        if (tickables.length === 0) continue;

        // Anacrusa: duración rítmica = contenido real escrito.
        const voiceNumBeats = isPickup
          ? Math.max(1, Math.round((renderEnd / divisions) * (beatType / 4)))
          : beatsPerMeasure;
        const vVoice = new Voice({ numBeats: voiceNumBeats, beatValue: beatType });
        vVoice.setStrict(false);
        vVoice.addTickables(tickables);
        vexVoices.push(vVoice);
        allNoteMaps.push(...noteMap);
        voiceBeamCandidates.set(vNum, beamCandidates);
      }

      // Formatear todas las voces juntas
      if (vexVoices.length > 0) {
        const minFormatWidth = isPickup ? Math.max(40, staveW - 60) : staveW - 40;
        new Formatter().joinVoices(vexVoices).format(vexVoices, minFormatWidth);
      }

      // Crear beams por voz (después del format para que las notas tengan posición)
      const allBeams = voiceNumbers.flatMap((vNum) => {
        const candidates: StaffBeamCandidate[] = (voiceBeamCandidates.get(vNum) || []).map(
          (c) => ({ note: c.note, beatIdx: c.beatIdx })
        );
        return buildVexBeamsFromCandidates(candidates);
      });

      // Dibujar voces y beams (trazo claro sobre fondo oscuro en darkMode)
      context.setStrokeStyle(notationColor);
      context.setFillStyle(notationColor);
      vexVoices.forEach((v) => v.draw(context, stave));
      drawVexBeams(allBeams, context, {
        fillStyle: notationColor,
        strokeStyle: notationColor,
      });

      for (const entry of allNoteMaps) {
        const { sn, flatIndices, gapStartTime } = entry;
        if (gapStartTime !== undefined) continue;
        const nHead0 = notes[flatIndices[0]];
        if (!nHead0) continue;
        const tHead = nHead0.startTime;
        const centers = noteHeadCentersFromStaveNote(sn as InstanceType<typeof StaveNote>);
        pushHalosDistinct(headGlowByTime, tHead, centers);
        flatIndices.forEach((flatIdx, idx) => {
          const center = centers[idx] ?? centers[0];
          if (center) headCenterByIndex[flatIdx] = center;
        });
      }

      for (const entry of allNoteMaps) {
        const { sn, flatIndices, gapStartTime } = entry;
        let att: number;
        let xBiasPre = 0;
        const isGapGlyph = gapStartTime !== undefined;
        if (isGapGlyph) {
          att = gapStartTime;
        } else {
          const n0 = notes[flatIndices[0]];
          if (!n0) continue;
          att = n0.startTime;
          if (!n0.isRest) {
            xBiasPre = STAFF_NOTEHEAD_CENTER_BIAS_PRESCALE;
          }
        }
        const cand = measureStartPx + (sn.getX() + xBiasPre) * STAVE_SCALE;
        if (mIdx === 0) {
          if (!isGapGlyph) {
            const nHead = notes[flatIndices[0]];
            if (nHead && !nHead.isRest) {
              firstMeasureMinContentPx =
                firstMeasureMinContentPx === undefined
                  ? cand
                  : Math.min(firstMeasureMinContentPx, cand);
            }
          }
        }
        if (isGapGlyph) {
          const prev = xGapByTime.get(att);
          xGapByTime.set(att, prev === undefined ? cand : Math.min(prev, cand));
        } else {
          const n0 = notes[flatIndices[0]];
          if (!n0) continue;
          if (n0.isRest) {
            const prev = xRestByTime.get(att);
            xRestByTime.set(att, prev === undefined ? cand : Math.min(prev, cand));
          } else {
            const prev = xPitchByTime.get(att);
            xPitchByTime.set(att, prev === undefined ? cand : Math.max(prev, cand));
          }
        }
      }

      globalNoteIndex += mNotes.length;

      if ((mIdx + 1) % MEASURES_PER_YIELD === 0) {
        await yieldToMain();
      }
    }

    if (measures.length > 0) {
      const m0 = measures[0].info;
      let minFromHeads: number | undefined;
      for (const key of Object.keys(headGlowByTime)) {
        const tt = Number(key);
        if (tt + 1e-9 >= m0.startTime && tt < m0.startTime + m0.length - 1e-9) {
          const heads = headGlowByTime[tt];
          if (heads?.length) {
            const lx = Math.min(...heads.map((h) => h.x));
            minFromHeads = minFromHeads === undefined ? lx : Math.min(minFromHeads, lx);
          }
        }
      }
      if (minFromHeads !== undefined) {
        firstMeasureMinContentPx = minFromHeads;
      }
    }

    // Fusionar: altura > silencio XML > hueco generado (anacrusa).
    const xByStartTime = new Map<number, number>();
    for (const [t, x] of xGapByTime) {
      if (!xPitchByTime.has(t) && !xRestByTime.has(t)) xByStartTime.set(t, x);
    }
    for (const [t, x] of xRestByTime) {
      if (!xPitchByTime.has(t)) xByStartTime.set(t, x);
    }
    for (const [t, x] of xPitchByTime) {
      xByStartTime.set(t, x);
    }

    const geom = [...xByStartTime.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([startTime, xFallback]) => {
        const heads = headGlowByTime[startTime];
        const x =
          heads && heads.length > 0
            ? heads.reduce((sum, h) => sum + h.x, 0) / heads.length
            : xFallback;
        return { startTime, x };
      });
    const geomSig = staffGeometrySignature(geom);
    if (geomSig !== lastPublishedGeomSigRef.current) {
      lastPublishedGeomSigRef.current = geomSig;
      onStaffTimeGeometryRef.current?.(geom);
      setStaffGeometry(geom);
      onStaffLayoutMetaRef.current?.({
        firstMeasureContentLeftPx: firstMeasureMinContentPx ?? geom[0]?.x ?? PADDING_X + 96,
        totalScrollWidthPx: totalWidth,
      });
      setHeadGlowCentersByTime(headGlowByTime);
      setHeadCentersByNoteIndex(headCenterByIndex);
    }
    };

    if (typeof requestIdleCallback !== "undefined") {
      idleHandle = requestIdleCallback(() => {
        void renderVex();
      }, { timeout: 800 });
    } else {
      idleHandle = window.setTimeout(() => {
        void renderVex();
      }, 16);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== undefined) {
        if (typeof cancelIdleCallback !== "undefined" && typeof idleHandle === "number") {
          cancelIdleCallback(idleHandle);
        } else {
          clearTimeout(idleHandle as ReturnType<typeof setTimeout>);
        }
      }
    };
  }, [
    notes,
    measures,
    measureWidths,
    timeSignature,
    totalWidth,
    beatsPerMeasure,
    beatType,
    guitar8vbWritten,
    divisions,
    pixelsPerBeat,
    measureInfos,
    darkMode,
    onStaffLayoutMeta,
    showBassStaff,
    canvasHeight,
    trebleStaveY,
    bassStaveY,
  ]);

  if (notes.length === 0) {
    return (
      <div className="w-full flex items-center justify-center rounded-xl border" style={{ height: STAVE_HEIGHT, backgroundColor: "#FAF6F0", borderColor: "#E8E0D4", color: "#8A8078", fontSize: "0.85rem", fontFamily: "'Inter', sans-serif" }}>
        Carga un MusicXML para ver el pentagrama
      </div>
    );
  }

  const staffBg = darkMode ? "transparent" : "#FAF6F0";

  return (
    <>
      <style>{`
        .staff-scroll::-webkit-scrollbar { display: none; }
        .staff-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
          contain: paint;
          overflow-anchor: none;
          -webkit-overflow-scrolling: touch;
        }
        .staff-scroll-active .staff-notehead-halo-outer,
        .staff-scroll-active .staff-notehead-halo-inner,
        .lane-scroll-active .staff-notehead-halo-outer,
        .lane-scroll-active .staff-notehead-halo-inner {
          animation-play-state: paused;
          filter: none;
          box-shadow: none;
        }
        @keyframes staff-notehead-halo-outer {
          0%, 100% { opacity: 0.72; transform: translate(-50%, -50%) scale(0.94); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.06); }
        }
        @keyframes staff-notehead-halo-inner {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.92; }
        }
        .staff-notehead-halo-outer {
          background: radial-gradient(
            ellipse 72% 70% at 50% 50%,
            color-mix(in srgb, var(--staff-halo-accent) 55%, white) 0%,
            color-mix(in srgb, var(--staff-halo-accent) 22%, transparent) 42%,
            transparent 72%
          );
          animation: staff-notehead-halo-outer 1.15s ease-in-out infinite;
          filter: saturate(1.18);
        }
        .staff-notehead-halo-inner {
          background: radial-gradient(
            ellipse 62% 58% at 48% 50%,
            color-mix(in srgb, var(--staff-halo-accent) 75%, #fff8f0) 0%,
            color-mix(in srgb, var(--staff-halo-accent) 35%, transparent) 55%,
            transparent 78%
          );
          animation: staff-notehead-halo-inner 1.15s ease-in-out infinite 0.06s;
          box-shadow:
            0 0 14px color-mix(in srgb, var(--staff-halo-accent) 45%, transparent),
            0 0 28px color-mix(in srgb, var(--staff-halo-accent) 22%, transparent);
        }
        @keyframes staff-feedback-pop {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
          18% { opacity: 1; transform: translate(-50%, -50%) scale(1.25); }
          100% { opacity: 0.95; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes staff-feedback-shake {
          0%, 100% { transform: translate(-50%, -50%) translateX(0); }
          25% { transform: translate(-50%, -50%) translateX(-2px); }
          50% { transform: translate(-50%, -50%) translateX(2px); }
          75% { transform: translate(-50%, -50%) translateX(-1px); }
        }
        .staff-feedback-note {
          animation: staff-feedback-pop 280ms ease-out both;
          border: 2px solid currentColor;
          box-shadow: 0 0 12px currentColor, inset 0 0 10px color-mix(in srgb, currentColor 35%, transparent);
          mix-blend-mode: screen;
        }
        .staff-feedback-note-correct {
          color: #34d399;
          background: rgba(52, 211, 153, 0.24);
        }
        .staff-feedback-note-wrong {
          color: #fb7185;
          background: rgba(251, 113, 133, 0.22);
          animation: staff-feedback-pop 220ms ease-out both, staff-feedback-shake 260ms ease-in-out 220ms 1;
        }
      `}</style>
      <div
        ref={scrollWrapRef}
        className="w-full staff-scroll"
        style={{ height: canvasHeight, backgroundColor: staffBg, overflow: "auto", position: "relative" }}
      >
        <div className="relative" style={{ width: totalWidth, height: canvasHeight }}>
          <StaffActiveGlowOverlay
            centersByStartTime={headGlowCentersByTime}
            geometry={staffGeometry}
            activeStartTime={activeStartTime}
            darkMode={darkMode}
            accent={activeGlowAccent}
            softOnly={hidePlayhead}
          />
          <StaffFeedbackOverlay centersByNoteIndex={headCentersByNoteIndex} feedback={noteFeedback} />
          <div ref={vexMountRef} className="relative z-[1]" style={{ display: "block" }} />
        </div>
      </div>
    </>
  );
};

export { SongStaffRenderer };
export default SongStaffRenderer;
